/**
 * Shared rule core — the single source of truth imported by BOTH the client
 * (solo practice + online render) and the authoritative Tikron room.
 *
 * Everything here is PURE: no DOM types, no Workers types, no `Date.now()` /
 * `performance.now()`, no side effects. Time advances only through
 * `RoundEngine.tick()` on a fixed 50 ms cadence and all randomness flows from a
 * seeded RNG, so a given (seed, input stream) yields identical results on the
 * server and every client. All state is plain, structure-clone-able JSON.
 *
 * Positions are normalized to [0, 1] "field space" (the mirrored, cover-fitted
 * camera view). Inputs are expressed as intents (`MouthSample`) — never
 * outcomes. This file must compile under both tsconfigs (server: workers-types,
 * no DOM; client: DOM) — keep it free of any environment globals.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Fixed simulation tick, matching Tikron's CasualRealtimeRoom sync cadence. */
export const TICK_MS = 50;

export const ROUND_MS = 30_000;
export const ROUNDS_TO_WIN = 2;
/** Hard bound on rounds per match (draws can replay, but never past this many). */
export const MAX_ROUNDS = 5;

/** Countdown before each round (server-driven, sim-clock ms). */
export const COUNTDOWN_MS = 3000;
/** Round-result overlay duration before the next round / match result. */
export const ROUND_RESULT_MS = 4000;
/** How long the server waits in `calibrate` for both players to ready up. */
export const CALIBRATE_TIMEOUT_MS = 90_000;

export const BOMB_KIND = "1f4a3";
export const PEPPER_KIND = "1f336";
export const BURN_MS = 2500;

/** Combo counts consecutive eat-closes within this window of the previous eat. */
const COMBO_WINDOW_MS = 2500;

/**
 * Ellipse expansion applied at hit-test time (PLAN §3: absorbs RTT — a food a
 * player visually mouthed a beat ago has moved slightly by the time the server
 * ticks). Applies uniformly to solo and online play — one rule set.
 */
export const MOUTH_HIT_EXPANSION = 1.15;

/**
 * Minimum sim-time between HONORED bites, per player (MAJOR-2). A closeSeq
 * bump arriving sooner still advances `prevCloseSeq` (so it can't be replayed
 * later) but eats nothing — this caps a tampered client spamming closeSeq at
 * ~4 eats/sec, matching plausible human bite cadence.
 */
export const MIN_BITE_INTERVAL_MS = 250;

/** Spawn cadence (ms) lerps from slow → fast across the round. */
const SPAWN_INTERVAL_START = 950;
const SPAWN_INTERVAL_END = 500;

/** Fall speed (field-heights / second) lerps slow → fast across the round. */
const FALL_VY_START = 0.22;
const FALL_VY_END = 0.42;
const FALL_VY_JITTER = 0.15;

const SPAWN_X_MIN = 0.08;
const SPAWN_X_MAX = 0.92;
const SPAWN_Y = -0.08;
const DESPAWN_Y = 1.08;

// ---------------------------------------------------------------------------
// Intents & food definitions
// ---------------------------------------------------------------------------

/**
 * One mouth sensor sample — the only thing a client will ever send upstream.
 * `closeSeq` increments on every OPEN→CLOSED transition of the local mouth
 * FSM, so the authority can detect a bite even if individual samples are
 * dropped or arrive between ticks.
 */
export interface MouthSample {
  /** Ellipse center in field space. */
  cx: number;
  cy: number;
  /** Ellipse radii as fractions of field width / height. */
  rx: number;
  ry: number;
  open: boolean;
  closeSeq: number;
}

export interface FoodDef {
  /** Twemoji codepoint — doubles as the sprite filename. */
  kind: string;
  points: number;
  weight: number;
  /** Display size as a fraction of field width. */
  size: number;
}

/** eat = positive points, bomb = penalty, pepper = temporary burn debuff. */
export const FOODS: readonly FoodDef[] = [
  { kind: "1f34e", points: 1, weight: 6, size: 0.09 }, // apple
  { kind: "1f34c", points: 1, weight: 6, size: 0.1 }, // banana
  { kind: "1f353", points: 1, weight: 6, size: 0.08 }, // strawberry
  { kind: "1f352", points: 1, weight: 5, size: 0.08 }, // cherries
  { kind: "1f351", points: 1, weight: 5, size: 0.09 }, // peach
  { kind: "1f36a", points: 1, weight: 5, size: 0.09 }, // cookie
  { kind: "1f955", points: 1, weight: 4, size: 0.09 }, // carrot
  { kind: "1f950", points: 1, weight: 4, size: 0.09 }, // croissant
  { kind: "1f359", points: 1, weight: 4, size: 0.09 }, // rice ball
  { kind: "1f355", points: 2, weight: 4, size: 0.1 }, // pizza
  { kind: "1f354", points: 2, weight: 4, size: 0.1 }, // burger
  { kind: "1f35f", points: 2, weight: 3, size: 0.09 }, // fries
  { kind: "1f32d", points: 2, weight: 3, size: 0.1 }, // hot dog
  { kind: "1f357", points: 2, weight: 3, size: 0.09 }, // chicken leg
  { kind: "1f369", points: 2, weight: 3, size: 0.09 }, // donut
  { kind: "1f347", points: 2, weight: 3, size: 0.09 }, // grapes
  { kind: "1f349", points: 2, weight: 2, size: 0.1 }, // watermelon
  { kind: "1f363", points: 3, weight: 3, size: 0.1 }, // sushi
  { kind: "1f382", points: 3, weight: 2, size: 0.11 }, // cake
  { kind: "1f370", points: 3, weight: 2, size: 0.1 }, // shortcake
  { kind: "1f4a3", points: -3, weight: 5, size: 0.1 }, // bomb
  { kind: "1f336", points: 0, weight: 3, size: 0.09 }, // hot pepper (burn)
] as const;

export interface Food {
  id: number;
  kind: string;
  /** Field space. y grows downward; spawn slightly above 0, despawn past 1. */
  x: number;
  y: number;
  /** Fall speed in field heights per second. */
  vy: number;
  size: number;
}

// ---------------------------------------------------------------------------
// Phase 4: penalty AR filters
// ---------------------------------------------------------------------------

export interface FilterDef {
  id: string;
  label: string;
  emoji: string;
  /** "overlay" = 2D SVG image anchored to a landmark (client/penalty.ts's FILTER_ANCHOR).
   *  "warp" = mesh liquify deformation, no image asset (client/warp.ts).
   *  "label" = meme-style text floating above the head, no image asset either
   *  (client/penalty.ts draws `label` directly via fillText — see drawLabelText()). */
  kind: "overlay" | "warp" | "label";
}

/** Hand-authored SVGs at `public/assets/filters/<id>.svg` — zero license risk. */
export const FILTERS = [
  { id: "mustache", label: "콧수염", emoji: "👨", kind: "overlay" },
  { id: "clown-nose", label: "광대코", emoji: "🤡", kind: "overlay" },
  { id: "pig-nose", label: "돼지코", emoji: "🐷", kind: "overlay" },
  { id: "glasses", label: "개그안경", emoji: "🤓", kind: "overlay" },
  { id: "tears", label: "눈물", emoji: "😭", kind: "overlay" },
  { id: "long-chin", label: "턱쭉", emoji: "🦒", kind: "warp" },
  { id: "skinny", label: "홀쭉이", emoji: "🥒", kind: "warp" },
  { id: "puffy", label: "볼빵빵", emoji: "🐹", kind: "warp" },
  { id: "ha-nam", label: "하남자", emoji: "🧍", kind: "label" },
  { id: "ha-yeo", label: "하여자", emoji: "🧍‍♀️", kind: "label" },
  { id: "potato-nam", label: "포테토남", emoji: "🥔", kind: "label" },
] as const satisfies readonly FilterDef[];

export type FilterId = (typeof FILTERS)[number]["id"];
export type OverlayFilterId = Extract<(typeof FILTERS)[number], { kind: "overlay" }>["id"];
export type WarpFilterId = Extract<(typeof FILTERS)[number], { kind: "warp" }>["id"];
export type LabelFilterId = Extract<(typeof FILTERS)[number], { kind: "label" }>["id"];

const FILTER_IDS: ReadonlySet<string> = new Set(FILTERS.map((f) => f.id));
const WARP_FILTER_IDS: ReadonlySet<string> = new Set(FILTERS.filter((f) => f.kind === "warp").map((f) => f.id));
const LABEL_FILTER_IDS: ReadonlySet<string> = new Set(FILTERS.filter((f) => f.kind === "label").map((f) => f.id));

export function isFilterId(v: unknown): v is FilterId {
  return typeof v === "string" && FILTER_IDS.has(v);
}

export function isWarpFilterId(v: unknown): v is WarpFilterId {
  return typeof v === "string" && WARP_FILTER_IDS.has(v);
}

export function isLabelFilterId(v: unknown): v is LabelFilterId {
  return typeof v === "string" && LABEL_FILTER_IDS.has(v);
}

export function isOverlayFilterId(v: unknown): v is OverlayFilterId {
  return isFilterId(v) && !WARP_FILTER_IDS.has(v) && !LABEL_FILTER_IDS.has(v);
}

// ---------------------------------------------------------------------------
// Round (per-round) state used by the solo wrapper
// ---------------------------------------------------------------------------

export interface PlayerRoundState {
  score: number;
  /** Sim-time (ms since round start) until which eating is disabled. */
  burnUntil: number;
}

export interface RoundState {
  running: boolean;
  elapsed: number;
  duration: number;
  foods: Food[];
  players: Record<string, PlayerRoundState>;
}

export type SimEvent =
  | { type: "eat"; player: string; kind: string; points: number; x: number; y: number; combo: number }
  | { type: "bomb"; player: string; points: number; x: number; y: number }
  | { type: "burn"; player: string; x: number; y: number }
  | { type: "miss"; kind: string; x: number }
  | { type: "end" };

// ---------------------------------------------------------------------------
// Match (multi-round) state — authoritative shape synced by the room
// ---------------------------------------------------------------------------

export type MatchPhase =
  | "lobby"
  | "calibrate"
  | "countdown"
  | "round"
  | "roundResult"
  | "matchResult";

export interface PlayerMatchState {
  /**
   * Seat number. Also doubles as the Phase 3 WebRTC "polite peer" assignment
   * (MDN perfect negotiation): slot 2 is polite, slot 1 is impolite. No
   * separate field — the client derives its role from its own slot in
   * `MatchState.players[myId].slot`.
   */
  slot: 1 | 2;
  name: string;
  ready: boolean;
  /** Current round score (reset each round). */
  score: number;
  /** Sim-time (ms since round start) until which this player's eating is disabled. */
  burnUntil: number;
  /** Rounds won this match. */
  wins: number;
  /** Cumulative score across every round played this match (draws included). */
  totalScore: number;
  /** True once this player has sent "rematch" on the current matchResult screen. */
  rematch: boolean;
}

export interface MatchState {
  phase: MatchPhase;
  players: Record<string, PlayerMatchState>;
  round: number;
  /** Sim-clock ms at which the current auto-advancing phase ends (countdown/roundResult). */
  phaseEndsAt: number;
  elapsed: number;
  duration: number;
  foods: Food[];
  lastRoundWinner: string | null;
  matchWinner: string | null;
  /** Message key when a match dies early (e.g. "opponent-left"); null otherwise. */
  aborted: string | null;
  /** Phase 4: the winner's chosen penalty filter id (FILTERS), or null until picked. */
  penaltyFilter: string | null;
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/** Small, fast, seeded PRNG. Deterministic across platforms. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Points are a property of the definition (FOODS), not the spawned Food. */
const POINTS_BY_KIND = new Map<string, number>(FOODS.map((f) => [f.kind, f.points]));

export function pointsOf(kind: string): number {
  return POINTS_BY_KIND.get(kind) ?? 0;
}

/** Weighted pick over FOODS using one draw from `rng`. */
export function pickFood(rng: () => number): FoodDef {
  let total = 0;
  for (const f of FOODS) total += f.weight;
  let r = rng() * total;
  let last: FoodDef | undefined;
  for (const f of FOODS) {
    last = f;
    r -= f.weight;
    if (r <= 0) return f;
  }
  if (last) return last;
  throw new Error("FOODS is empty");
}

/**
 * Ellipse hit test: is `food`'s center inside the mouth ellipse of `s`?
 * `expansion` scales both radii before testing (default 1 = no expansion);
 * the engine's own flagged-set recompute uses {@link MOUTH_HIT_EXPANSION}.
 */
export function inMouth(food: Food, s: MouthSample, expansion = 1): boolean {
  const rx = s.rx * expansion;
  const ry = s.ry * expansion;
  if (rx <= 0 || ry <= 0) return false;
  const dx = (food.x - s.cx) / rx;
  const dy = (food.y - s.cy) / ry;
  return dx * dx + dy * dy <= 1;
}

// ---------------------------------------------------------------------------
// RoundEngine — the shared per-round simulation
// ---------------------------------------------------------------------------

interface EnginePlayer {
  score: number;
  /** Sim-time (ms since round start) until which eating is disabled. */
  burnUntil: number;
  sample: MouthSample | null;
  prevCloseSeq: number;
  /** Food ids currently inside the (last-open) mouth ellipse. */
  flagged: Set<number>;
  combo: number;
  lastEatAt: number;
  /** Sim-time of the last HONORED bite (MIN_BITE_INTERVAL_MS rate limit). */
  lastBiteAt: number;
}

/**
 * Deterministic per-round simulation shared by the solo `GameSim` wrapper and
 * the authoritative server room. Owns the food timeline (spawn cadence, fall,
 * despawn) and the eating rules (flagged-set semantics, scoring, combo, burn).
 * The ONLY input is the `MouthSample` intent; time advances only via `tick()`.
 */
export class RoundEngine {
  running = false;
  elapsed = 0;
  duration = 0;
  /** Live food list — plain, structure-clone-able; safe to point room state at. */
  foods: Food[] = [];

  private rng: () => number = mulberry32(1);
  private players = new Map<string, EnginePlayer>();
  private playerIds: string[] = [];
  private nextFoodId = 1;
  private nextSpawnAt = 0;

  start(seed: number, durationMs: number, playerIds: string[]): void {
    this.rng = mulberry32(seed);
    this.running = true;
    this.elapsed = 0;
    this.duration = durationMs;
    this.foods = [];
    this.players = new Map();
    this.playerIds = [...playerIds];
    this.nextFoodId = 1;
    for (const pid of playerIds) {
      this.players.set(pid, {
        score: 0,
        burnUntil: 0,
        sample: null,
        // -1 = "unseeded": no sample has been applied yet this round. Seeded to
        // the first incoming closeSeq in applyMouth() so a mouth FSM that was
        // reused/left mid-cycle from a previous round can't register a spurious
        // bite on the round's first tick.
        prevCloseSeq: -1,
        flagged: new Set(),
        combo: 0,
        lastEatAt: -Infinity,
        lastBiteAt: -Infinity,
      });
    }
    this.nextSpawnAt = this.spawnInterval();
  }

  applyMouth(playerId: string, s: MouthSample): void {
    const rt = this.players.get(playerId);
    if (!rt) return;
    if (rt.prevCloseSeq === -1) rt.prevCloseSeq = s.closeSeq;
    rt.sample = s;
  }

  ids(): string[] {
    return this.playerIds;
  }

  scoreOf(playerId: string): number {
    return this.players.get(playerId)?.score ?? 0;
  }

  burnUntilOf(playerId: string): number {
    return this.players.get(playerId)?.burnUntil ?? 0;
  }

  tick(): SimEvent[] {
    const events: SimEvent[] = [];
    if (!this.running) return events;

    this.elapsed += TICK_MS;

    // --- spawning ---
    while (this.elapsed < this.duration && this.elapsed >= this.nextSpawnAt) {
      this.spawnFood();
      this.nextSpawnAt += this.spawnInterval();
    }

    // --- fall ---
    const dt = TICK_MS / 1000;
    for (const food of this.foods) {
      food.y += food.vy * dt;
    }

    // --- per-player mouth logic ---
    const eatenIds = new Set<number>();
    for (const pid of this.playerIds) {
      const rt = this.players.get(pid);
      if (!rt) continue;
      const s = rt.sample;
      if (!s) continue;

      // Recompute the flagged set ONLY while the mouth is open and the player is
      // not burned. While closed (or burned) we freeze the set so a bite eats
      // exactly what was under the mouth on the last open frame. The hit test
      // expands the ellipse to absorb RTT (PLAN §3).
      if (s.open && this.elapsed >= rt.burnUntil) {
        const next = new Set<number>();
        for (const food of this.foods) {
          if (inMouth(food, s, MOUTH_HIT_EXPANSION)) next.add(food.id);
        }
        rt.flagged = next;
      }

      // A bite: OPEN→CLOSED transition bumped closeSeq. The bump always
      // advances prevCloseSeq (so it can never retrigger later), but it is
      // only HONORED — i.e. actually allowed to eat — when the sample carrying
      // it claims the mouth is CLOSED (open === false, matching the real
      // client FSM, which flips open false on the exact sample that bumps
      // closeSeq) AND enough sim-time has passed since the last honored bite
      // (MIN_BITE_INTERVAL_MS). MAJOR-2: without both gates, a tampered client
      // sending an always-open giant ellipse with closeSeq++ every tick could
      // eat everything in range at the full tick rate.
      if (s.closeSeq > rt.prevCloseSeq) {
        rt.prevCloseSeq = s.closeSeq;
        const honored = !s.open && this.elapsed - rt.lastBiteAt >= MIN_BITE_INTERVAL_MS;
        if (honored) {
          rt.lastBiteAt = this.elapsed;
          const toEat = this.foods.filter((f) => rt.flagged.has(f.id) && !eatenIds.has(f.id));
          rt.flagged = new Set();
          if (toEat.length > 0) {
            const hasPointFood = toEat.some((f) => f.kind !== BOMB_KIND && f.kind !== PEPPER_KIND);
            let combo = 0;
            if (hasPointFood) {
              combo = this.elapsed - rt.lastEatAt <= COMBO_WINDOW_MS ? rt.combo + 1 : 1;
              rt.combo = combo;
              rt.lastEatAt = this.elapsed;
            }
            for (const food of toEat) {
              eatenIds.add(food.id);
              if (food.kind === BOMB_KIND) {
                rt.score = Math.max(0, rt.score - 3);
                events.push({ type: "bomb", player: pid, points: pointsOf(food.kind), x: food.x, y: food.y });
              } else if (food.kind === PEPPER_KIND) {
                rt.burnUntil = this.elapsed + BURN_MS;
                events.push({ type: "burn", player: pid, x: food.x, y: food.y });
              } else {
                const base = pointsOf(food.kind);
                const points = combo >= 3 ? base + 1 : base;
                rt.score += points;
                events.push({ type: "eat", player: pid, kind: food.kind, points, x: food.x, y: food.y, combo });
              }
            }
          }
        }
      }
    }
    if (eatenIds.size > 0) {
      this.foods = this.foods.filter((f) => !eatenIds.has(f.id));
    }

    // --- despawn / miss ---
    if (this.foods.some((f) => f.y > DESPAWN_Y)) {
      const survivors: Food[] = [];
      for (const food of this.foods) {
        if (food.y > DESPAWN_Y) events.push({ type: "miss", kind: food.kind, x: food.x });
        else survivors.push(food);
      }
      this.foods = survivors;
    }

    // --- end of round (emit exactly once) ---
    if (this.elapsed >= this.duration) {
      this.running = false;
      events.push({ type: "end" });
    }

    return events;
  }

  private spawnInterval(): number {
    const t = this.duration > 0 ? Math.min(this.elapsed / this.duration, 1) : 0;
    return lerp(SPAWN_INTERVAL_START, SPAWN_INTERVAL_END, t);
  }

  private spawnFood(): void {
    const def = pickFood(this.rng);
    const t = this.duration > 0 ? Math.min(this.elapsed / this.duration, 1) : 0;
    const base = lerp(FALL_VY_START, FALL_VY_END, t);
    const vy = base * (1 + (this.rng() * 2 - 1) * FALL_VY_JITTER);
    const x = SPAWN_X_MIN + this.rng() * (SPAWN_X_MAX - SPAWN_X_MIN);
    this.foods.push({
      id: this.nextFoodId++,
      kind: def.kind,
      x,
      y: SPAWN_Y,
      vy,
      size: def.size,
    });
  }
}
